/**
 * Ed25519 signatures that let the admin's browser, and every device, trust
 * what the Hub relays without trusting the Hub. Mirrors
 * `zunox_crypto::signing` byte for byte; the test vectors in
 * orgSigning.test.node.ts pin the layouts on both sides.
 *
 * An organization has a long-lived **identity** key. It signs each key
 * activation (which encryption key devices use) and each device
 * endorsement (this signing key belongs to that device). Devices sign every
 * record they send with their own key, inside the encryption.
 *
 * Admins hold one secret string that carries both the identity and the
 * current encryption key: `zxk2.<identity>.<encryption>`. A key from before
 * signing existed (plain base64, encryption key only) still decrypts, but
 * can't sign anything.
 *
 * The engine service's account is given less: `zxe1.<identity public
 * key>.<encryption>` (see `engineShare`). It reads records and checks
 * signatures but never signs, so the identity's signing key never reaches
 * it, nor a copy sealed to it on the Hub.
 */
import { ed25519, x25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { base64ToBytes, base64UrlToBytes, bytesToBase64, bytesToBase64Url, bytesToHex, concatBytes, } from '~/util/bytes';
const encoder = new TextEncoder();
const ACTIVATION_CONTEXT = encoder.encode('zunox/org-key-activation/v1\0');
const ENDORSEMENT_CONTEXT = encoder.encode('zunox/device-endorsement/v1\0');
const RECORD_CONTEXT = encoder.encode('zunox/record/v1\0');
/** Leads every signed record; an unsigned one starts with `{`. */
export const RECORD_MAGIC = encoder.encode('ZXS1');
const SIGNATURE_LEN = 64;
const SECRET_PREFIX = 'zxk2.';
const ENGINE_PREFIX = 'zxe1.';
export class OrgSecretError extends Error {
}
/** Reads a pasted organization secret, either format. */
export function parseOrgSecret(text) {
    const value = text.trim();
    try {
        if (value.startsWith(SECRET_PREFIX)) {
            const [identity, encryption] = value.slice(SECRET_PREFIX.length).split('.');
            const identitySeed = base64UrlToBytes(identity ?? '');
            const encryptionKey = base64UrlToBytes(encryption ?? '');
            if (identitySeed.length === 32 && encryptionKey.length === 32) {
                return { identitySeed, encryptionKey };
            }
        }
        else {
            const encryptionKey = base64ToBytes(value);
            if (encryptionKey.length === 32)
                return { identitySeed: null, encryptionKey };
        }
    }
    catch {
        // Falls through to the error below.
    }
    throw new OrgSecretError("That isn't an organization key.");
}
/** The secret string an admin keeps: identity and encryption key together. */
export function formatOrgSecret(identitySeed, encryptionKey) {
    return `${SECRET_PREFIX}${bytesToBase64Url(identitySeed)}.${bytesToBase64Url(encryptionKey)}`;
}
/** What's shared with the engine service's account: the encryption key and
 * the identity's public key, never its signing seed. Null for a key from
 * before signing existed, which the engine doesn't take. */
export function engineShare(text) {
    const { identitySeed, encryptionKey } = parseOrgSecret(text);
    if (!identitySeed)
        return null;
    const identity = ed25519.getPublicKey(identitySeed);
    return `${ENGINE_PREFIX}${bytesToBase64Url(identity)}.${bytesToBase64Url(encryptionKey)}`;
}
/** The X25519 private key in a pasted secret. */
export function encryptionKeyOf(text) {
    return parseOrgSecret(text).encryptionKey;
}
export function generateIdentitySeed() {
    return ed25519.utils.randomSecretKey();
}
export function generateEncryptionKey() {
    return x25519.utils.randomSecretKey();
}
export function encryptionPublicKey(encryptionKey) {
    return bytesToBase64(x25519.getPublicKey(encryptionKey));
}
export function identityPublicKey(identitySeed) {
    return bytesToBase64(ed25519.getPublicKey(identitySeed));
}
/**
 * A short, readable digest of an identity public key, to compare by eye
 * between the admin console and a device: `3F2A 9C10 …` (eight groups).
 * Matches `zunox_crypto::signing::fingerprint`.
 */
export function fingerprint(publicKeyBase64) {
    const digest = bytesToHex(sha256(base64ToBytes(publicKeyBase64)).subarray(0, 16)).toUpperCase();
    return digest.match(/.{4}/g)?.join(' ') ?? digest;
}
/** The fingerprint staff verify for an account: of its account key alone, or,
 * for the engine's account, of its account key and its signing key together,
 * as the engine prints it (zunox-puller `Account::fingerprint`). */
export function accountFingerprint(publicKeyBase64, signingKeyBase64) {
    if (!signingKeyBase64)
        return fingerprint(publicKeyBase64);
    return fingerprint(bytesToBase64(concatBytes(base64ToBytes(publicKeyBase64), base64ToBytes(signingKeyBase64))));
}
/** A UUID's 16 bytes. */
export function uuidBytes(uuid) {
    const hex = uuid.replace(/-/g, '');
    if (!/^[0-9a-fA-F]{32}$/.test(hex))
        throw new Error(`not a UUID: ${uuid}`);
    return Uint8Array.from(hex.match(/../g), byte => parseInt(byte, 16));
}
function u64be(value) {
    const bytes = new Uint8Array(8);
    new DataView(bytes.buffer).setBigUint64(0, BigInt(value));
    return bytes;
}
function i32be(value) {
    const bytes = new Uint8Array(4);
    new DataView(bytes.buffer).setInt32(0, value);
    return bytes;
}
/** The statement making `encPublicKey` (version `keyVersion`) current. */
export function activationMessage(orgId, seq, keyVersion, encPublicKeyBase64) {
    return concatBytes(ACTIVATION_CONTEXT, uuidBytes(orgId), u64be(seq), i32be(keyVersion), base64ToBytes(encPublicKeyBase64));
}
/** The statement vouching that a signing key belongs to a device. */
export function endorsementMessage(orgId, deviceId, deviceSigningKeyBase64) {
    return concatBytes(ENDORSEMENT_CONTEXT, uuidBytes(orgId), uuidBytes(deviceId), base64ToBytes(deviceSigningKeyBase64));
}
export function signWithIdentity(identitySeed, message) {
    return bytesToBase64(ed25519.sign(message, identitySeed));
}
export function verifySignature(publicKeyBase64, message, signatureBase64) {
    try {
        return ed25519.verify(base64ToBytes(signatureBase64), message, base64ToBytes(publicKeyBase64), {
            zip215: false,
        });
    }
    catch {
        return false;
    }
}
function startsWithMagic(bytes) {
    return RECORD_MAGIC.every((b, i) => bytes[i] === b);
}
/**
 * Checks a decrypted record against its device's signing key. A record from
 * an agent older than signing has no signature at all (`unsigned`); a
 * signature that fails, or that can't be checked because the device has no
 * registered key, makes it `invalid`.
 */
export function openRecord(bytes, deviceSigningKeyBase64) {
    if (!startsWithMagic(bytes))
        return { status: 'unsigned', body: bytes };
    const rest = bytes.subarray(RECORD_MAGIC.length);
    if (rest.length < SIGNATURE_LEN || !deviceSigningKeyBase64) {
        return { status: 'invalid', body: null };
    }
    const signature = rest.subarray(0, SIGNATURE_LEN);
    const body = rest.subarray(SIGNATURE_LEN);
    try {
        const ok = ed25519.verify(signature, concatBytes(RECORD_CONTEXT, body), base64ToBytes(deviceSigningKeyBase64), { zip215: false });
        return ok ? { status: 'signed', body } : { status: 'invalid', body: null };
    }
    catch {
        return { status: 'invalid', body: null };
    }
}
